import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Link as LinkIcon, X } from 'lucide-react';
import { createTodo } from '../api/todo.service';
import { getTasks } from '../api/task.service';
import { DateTimeField } from './DateTimeField.jsx';
import { Select, SelectItem } from './Select';
import { toIsoFromDateTimeLocal } from '../lib/dateInputs';
import useAuthStore from '../store/useAuthStore';
import useUIStore from '../store/useUIStore';

const fetchAssignedTasks = async (userId) => {
  if (!userId) return [];
  const { data } = await getTasks({ assigneeId: userId });
  return data.data || [];
};

export default function NoteFormModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();

  const [formData, setFormData] = useState({
    note: '',
    scheduledFor: '',
    taskId: '',
  });

  const { data: assignedTasks } = useQuery({
    queryKey: ['assigned-tasks', user?.id],
    queryFn: () => fetchAssignedTasks(user?.id),
    enabled: !!user && isOpen,
  });

  const createMutation = useMutation({
    mutationFn: (newTodo) => createTodo(newTodo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-todos'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      addToast({ type: 'success', message: 'Note created successfully.' });
      onClose();
    },
    onError: () => {
      addToast({ type: 'error', message: 'Failed to create note.' });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.note.trim()) return;

    const payload = {
      note: formData.note,
      scheduledFor: formData.scheduledFor ? toIsoFromDateTimeLocal(formData.scheduledFor) : null,
      taskId: formData.taskId || null,
    };

    createMutation.mutate(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 border border-border">
        <div className="px-4 sm:px-6 py-4 border-b border-border flex items-center justify-between bg-muted/50">
          <h2 className="text-lg font-bold text-foreground">New Note</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-card hover:text-foreground cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-card space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-foreground">
              Note Content *
            </label>
            <textarea
              rows={4}
              required
              placeholder="Type your quick note here..."
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none resize-none text-sm"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-foreground">
              <Calendar className="w-3.5 h-3.5" />
              Schedule For
            </label>
            <DateTimeField
              type="datetime-local"
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm"
              value={formData.scheduledFor}
              onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
            />
          </div>

          <div>
            <label className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-foreground">
              <LinkIcon className="w-3.5 h-3.5" />
              Link to Project Task
            </label>
            <Select
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none truncate text-sm"
              value={formData.taskId}
              onChange={(e) => setFormData({ ...formData, taskId: e.target.value })}
            >
              <SelectItem value="">No linked task</SelectItem>
              {assignedTasks?.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.title} ({t.module?.name || 'No module'})
                </SelectItem>
              ))}
            </Select>
            <p className="mt-1 text-[10px] text-muted-foreground italic">
              Only tasks explicitly assigned to you are shown.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-2 border border-input text-foreground rounded-xl font-medium hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 bg-primary hover:opacity-90 text-primary-foreground px-4 py-2 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm"
            >
              {createMutation.isPending && (
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              )}
              {createMutation.isPending ? 'Saving...' : 'Create Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
